import type {
  FilterDefinition,
  FilterDefinitionActiveState,
} from './filterDefinitions'
import type { OperatorArrayFilter } from './utils/queryParams'

export interface FacetValuesState<Value> {
  isAuthorizedBaseScopeResponse: boolean
  isPlaceholderData: boolean
  status: 'error' | 'pending' | 'ready'
  values: ReadonlyArray<Value>
}

interface CommonFilterDefinitionOptions<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
  Property extends string,
> extends Omit<
  FilterDefinition<
    StateParams,
    CommandGroupParams,
    SelectedParams,
    SelectorParams,
    OperatorParams
  >,
  'active' | 'clear' | 'property'
> {
  clear: (params: StateParams) => void
  property: Property
}

interface EnumFilterDefinitionOptions<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
  Property extends string,
  Value,
> extends CommonFilterDefinitionOptions<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
  Property
> {
  getValue: (params: StateParams) => ReadonlyArray<Value>
}

export function createEnumFilterDefinition<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
  Property extends string,
  Value,
>({
  getValue,
  ...options
}: EnumFilterDefinitionOptions<
  StateParams,
  CommandGroupParams,
  SelectedParams,
  SelectorParams,
  OperatorParams,
  Property,
  Value
>) {
  return {
    ...options,
    active: (params) => {
      return createArrayFilterActiveState(getValue(params))
    },
  } satisfies FilterDefinition<
    StateParams,
    CommandGroupParams,
    SelectedParams,
    SelectorParams,
    OperatorParams
  > & { property: Property }
}

export function createArrayFilterActiveState<Value>(
  value: ReadonlyArray<Value>,
) {
  return createCountActiveState(value.length)
}

export function createOperatorArrayFilterActiveState<Value>(
  value: OperatorArrayFilter<Value>,
) {
  return createCountActiveState(value.values.length)
}

export function cleanupStaleFacetValues<Value>({
  facetValuesState,
  selectedValues,
}: {
  facetValuesState: FacetValuesState<Value>
  selectedValues: ReadonlyArray<Value>
}) {
  const isProvenInvalidityResponse =
    facetValuesState.status === 'ready' &&
    facetValuesState.isAuthorizedBaseScopeResponse &&
    facetValuesState.isPlaceholderData === false

  if (isProvenInvalidityResponse) {
    return selectedValues.filter((selectedValue) => {
      return facetValuesState.values.includes(selectedValue)
    })
  }

  return [...selectedValues]
}

export interface FilterRequestParamApplier<StateParams, RequestParams> {
  applyRequestParams?: (params: {
    requestParams: Partial<RequestParams>
    stateParams: StateParams
  }) => void
}

export function applyFilterRequestParams<
  Definition,
  StateParams,
  RequestParams,
>({
  filterDefinitions,
  requestParams,
  stateParams,
}: {
  filterDefinitions: Array<
    Definition & FilterRequestParamApplier<StateParams, RequestParams>
  >
  requestParams: Partial<RequestParams>
  stateParams: StateParams
}) {
  for (const definition of filterDefinitions) {
    definition.applyRequestParams?.({ requestParams, stateParams })
  }

  return requestParams
}

export function createFilterRequestParamApplier<
  StateParams,
  Value,
  RequestValue,
  RequestParams,
  Key extends keyof RequestParams,
>({
  getValue,
  key,
  serialize,
}: {
  getValue: (params: StateParams) => Value
  key: Key
  serialize: (params: {
    stateParams: StateParams
    value: Value
  }) => (RequestValue & NonNullable<RequestParams[Key]>) | undefined
}) {
  return ({
    requestParams,
    stateParams,
  }: {
    requestParams: Partial<RequestParams>
    stateParams: StateParams
  }) => {
    const value = getValue(stateParams)
    const requestValue = serialize({ stateParams, value })
    if (requestValue !== undefined) {
      Object.assign(requestParams, { [key]: requestValue })
    }
  }
}

function createCountActiveState(count: number): FilterDefinitionActiveState {
  return count > 0
    ? {
        badge: count,
        count,
      }
    : {
        count: 0,
      }
}
